import { useMemo, FC, useState } from 'react';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { getDate } from 'utils/convertString';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { AvatarIcon } from 'components/Core/AvatarIcon';
import { TableActionsButtons } from 'components/RequestsComponents/TableActionsButtons';
import RequestsTableFiltersForm from 'components/RequestsComponents/RequestsTableFiltersForm';
import { IProductRequest } from 'stores/requests/types';
import { pointsRequestsTableTranslations } from './translations';
import { PointsRequestsTableTransFields } from './types';
import styles from './productsRequestsTable.module.scss';

export const ProductsRequestsTable: FC = observer(() => {
  const {
    requestsStore: { productRequests },
  } = useStore();

  const { t } = useMyTranslations<PointsRequestsTableTransFields>([pointsRequestsTableTranslations.name]);

  const navigate = useNavigate();

  const [filtred, setFiltred] = useState<IProductRequest[]>([]);

  const columns: Column<IProductRequest>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original: { name, imageUrl, id },
          },
        }: CellProps<IProductRequest>) => {
          return (
            <div className={styles.avatar} onClick={() => navigate(`${id}`)}>
              <AvatarIcon avatar={imageUrl} name='imageUrl' size={56} styleProps={{ borderRadius: 8 }} />
              <p className={styles.name}>{name}</p>
            </div>
          );
        },
        accessor: ({ name }) => name,
        width: 2,
      },
      {
        Header: t('date'),
        id: 'date',
        Cell: ({
          row: {
            original: { createdAt },
          },
        }: CellProps<IProductRequest>) => <p className={styles.name}>{getDate(createdAt)}</p>,
        accessor: ({ createdAt }) => +new Date(createdAt),
        width: 1,
      },
      {
        Header: t('brandName'),
        id: 'brandName',
        accessor: ({ brand: { name } }) => name,
        width: 1,
      },
      {
        Header: t('owner'),
        id: 'owner',
        Cell: ({
          row: {
            original: {
              owner: { name, email },
            },
          },
        }: CellProps<IProductRequest>) => {
          return (
            <div className={styles.contacts}>
              <span className={styles.contactsPhone}>{name}</span>
              <span className={styles.contactsEmail}>{email}</span>
            </div>
          );
        },
        accessor: ({ owner: { name } }) => name,
        width: 1,
      },
      {
        Header: t('description'),
        id: 'description',
        Cell: ({
          row: {
            original: { description },
          },
        }: CellProps<IProductRequest>) => {
          return <p className={styles.description}>{description}</p>;
        },
        accessor: ({ description }) => description,
        width: 1,
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({
          row: {
            original: { id, status },
          },
        }: CellProps<IProductRequest>) => <TableActionsButtons id={id} entity='products' status={status} />,
        width: 4,
        disableSortBy: true,
      },
    ],
    [productRequests]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return productRequests && productRequests.length > 0 ? (
    <>
      <RequestsTableFiltersForm data={productRequests} setFiltredData={setFiltred} />
      <Table
        columns={columns as never}
        data={[...filtred]}
        simpleList
        isPaginated={productRequests.length > PAGE_SIZE}
      />
    </>
  ) : (
    <NoDataComponent />
  );
});
